import { formatTime, startOfDay } from '@crm/shared';
import { Card, CardContent, Spinner } from '@crm/ui';
import { ArrowRight, CalendarCheck, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from '@/components';
import { SCHEDULE_ROUTES, useAgenda } from '../api';

/**
 * A fila de hoje no painel: quem esta por vir, na ordem do relogio. E o
 * mesmo dado da tela de atendimentos, resumido - daqui se pula para la.
 */
export function TodayQueue({ limit = 12 }: { limit?: number }) {
  const [size, setSize] = useState(limit);
  const hoje = startOfDay(new Date());
  const fim = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 23, 59, 59);

  const agenda = useAgenda({ from: hoje, to: fim });

  const todos = (agenda.data?.data ?? []).filter(
    (item) => item.status === 'SCHEDULED' || item.status === 'CONFIRMED',
  );

  const fila = (agenda.data?.data ?? [])
    .filter((item) => item.status === 'SCHEDULED' || item.status === 'CONFIRMED')
    .sort((a, b) => a.startAt.localeCompare(b.startAt))
    .slice(0, size);

  return (
    // Titulo dentro do cartao, como nas listagens do sistema: o quadro branco
    // guarda a lista e o nome dela.
    <Card className="-mx-3 flex h-full flex-col rounded-none border-x-0 bg-card sm:mx-0 sm:rounded-xl sm:border-x dark:bg-white/[0.06]">
      <CardContent className="flex-1 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold tracking-tight">Fila de atendimentos</h2>
          <Link
            to={SCHEDULE_ROUTES.SERVICE_DESK}
            className="flex items-center gap-1 text-sm text-primary hover:underline"
          >
            Ver todos
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div>
          {agenda.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : fila.length === 0 ? (
            /* Fila vazia nao e erro nem falta: ou o dia ainda nao comecou,
               ou todo mundo ja foi atendido. Uma frase seca no meio do
               branco parecia tela quebrada. */
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                <CalendarCheck className="h-6 w-6" />
              </span>

              <div className="space-y-1">
                <p className="text-sm font-medium">Fila vazia</p>
                <p className="text-sm text-muted-foreground">
                  Ninguem esperando agora. Quem marcar para hoje aparece aqui na hora.
                </p>
              </div>

              <Link
                to={SCHEDULE_ROUTES.AGENDA}
                className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
              >
                Abrir a agenda
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {fila.map((item) => (
                <li key={item.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <span className="w-12 shrink-0 text-sm font-medium tabular-nums">
                    {formatTime(item.startAt)}
                  </span>

                  <Avatar
                    name={item.customerName}
                    image={item.customerImage}
                    className="h-9 w-9 shrink-0"
                  />

                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{item.customerName}</span>
                    {/* Todos os servicos da comanda, um por linha: e o que o
                      balcao precisa saber antes de chamar o cliente. */}
                    <span className="block text-xs text-muted-foreground">
                      {item.items.map((servico) => servico.serviceName).join(' - ')}
                    </span>
                  </span>

                  <Link
                    to={SCHEDULE_ROUTES.detail(item.id)}
                    aria-label={`Abrir o agendamento de ${item.customerName}`}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {/* O resto da fila fica a um toque: a lista inteira no painel
              empurrava tudo que vem depois para fora da tela. */}
          {todos.length > fila.length ? (
            <button
              type="button"
              onClick={() => setSize((atual) => atual + limit)}
              className="mt-3 w-full text-sm font-medium text-primary transition-colors hover:text-primary/80"
            >
              Ver mais
            </button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
